/**
 * Stock error types matching backend exception types
 */
export const StockErrorType = {
  OUT_OF_STOCK: 'out_of_stock',
  INSUFFICIENT_STOCK: 'insufficient_stock',
  STOCK_ADJUSTED: 'stock_adjusted',
  PRODUCTS_REMOVED: 'products_removed',
  STOCK_VALIDATION_FAILED: 'stock_validation_failed',
} as const;

export type StockErrorType = typeof StockErrorType[keyof typeof StockErrorType];

/**
 * Stock error response from API
 */
export interface StockErrorResponse {
  status: 'error';
  type: StockErrorType;
  message: string;
  product?: string;
  available_stock?: number | null;
  requested_quantity?: number | null;
  details?: {
    removed_products?: string[];
    [key: string]: any;
  };
}

/**
 * Other error types used in the app
 */
export const ErrorType = {
  // Stock errors
  ...StockErrorType,

  // Payment errors
  ERROR_PAYMENT_NL: 'error_payment_nl',
  ERROR_PAYMENT_BE: 'error_payment_be',

  // Auth errors
  NOT_AUTHORIZED: 'not_authorized',

  // System errors
  PLATFORM_NOT_SPECIFIED: 'platform_not_specified',
  REDIRECT_ERROR_RESPONSE: 'redirect_error_response',
  EXCEPTION: 'exception',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

/**
 * Type guard to check if error is a stock error
 */
export function isStockError(error: any): error is StockErrorResponse {
  return error?.type && Object.values(StockErrorType).includes(error.type);
}

/**
 * Type guard to check if error is out of stock
 */
export function isOutOfStock(error: any): boolean {
  return error?.type === StockErrorType.OUT_OF_STOCK;
}

/**
 * Type guard to check if error is insufficient stock
 */
export function isInsufficientStock(error: any): boolean {
  return error?.type === StockErrorType.INSUFFICIENT_STOCK;
}

/**
 * Get user-friendly error message based on error type
 */
export function getStockErrorMessage(error: StockErrorResponse): string {
  switch (error.type) {
    case StockErrorType.OUT_OF_STOCK:
      return error.product
        ? `${error.product} is uitverkocht`
        : 'Product is uitverkocht';

    case StockErrorType.INSUFFICIENT_STOCK:
      return error.product && error.available_stock !== undefined
        ? `Slechts ${error.available_stock} ${error.product} beschikbaar`
        : error.message || 'Onvoldoende voorraad';

    case StockErrorType.STOCK_ADJUSTED:
      return error.message || 'Aantal aangepast vanwege beperkte voorraad';

    case StockErrorType.PRODUCTS_REMOVED:
      if (error.details?.removed_products?.length === 1) {
        return `${error.details.removed_products[0]} is verwijderd omdat deze niet meer op voorraad is`;
      } else if (error.details?.removed_products?.length) {
        return `${error.details.removed_products.join(', ')} zijn verwijderd omdat ze niet meer op voorraad zijn`;
      }
      return error.message || 'Producten verwijderd vanwege voorraad';

    case StockErrorType.STOCK_VALIDATION_FAILED:
      return error.message || 'Voorraad validatie mislukt';

    default:
      return error.message || 'Er is een voorraad probleem opgetreden';
  }
}